import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./Drawer.module.css";

interface DrawerProps {
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
	title?: string;
	ariaLabel?: string;
}

const Drawer: React.FC<DrawerProps> = ({
	isOpen,
	onClose,
	children,
	title,
	ariaLabel
}) => {
	const drawerRef = useRef<HTMLDivElement>(null);
	const previousActiveElement = useRef<HTMLElement | null>(null);
	const titleId = useRef(`drawer-title-${Math.random().toString(36).substr(2, 9)}`);

	useEffect(() => {
		if (!isOpen) return;

		previousActiveElement.current = document.activeElement as HTMLElement;

		if (drawerRef.current) {
			drawerRef.current.focus();
		}

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		const handleTab = (e: KeyboardEvent) => {
			if (e.key !== "Tab" || !drawerRef.current) return;

			const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			const firstFocusable = focusableElements[0];
			const lastFocusable = focusableElements[focusableElements.length - 1];

			if (e.shiftKey) {
				// Shift + Tab
				if (document.activeElement === firstFocusable) {
					e.preventDefault();
					lastFocusable?.focus();
				}
			} else {
				// Tab
				if (document.activeElement === lastFocusable) {
					e.preventDefault();
					firstFocusable?.focus();
				}
			}
		};

		document.addEventListener("keydown", handleEscape);
		document.addEventListener("keydown", handleTab);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.removeEventListener("keydown", handleTab);
			document.body.style.overflow = "";

			// Restore focus to the previously focused element
			if (previousActiveElement.current) {
				previousActiveElement.current.focus();
			}
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return createPortal(
		<div
			className={styles.drawerOverlay}
			onClick={onClose}
			role="presentation"
		>
			<div
				ref={drawerRef}
				className={styles.drawerContent}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby={title ? titleId.current : undefined}
				aria-label={!title ? (ariaLabel ?? 'Dialog') : undefined}
				tabIndex={-1}
			>
				<div className={styles.drawerHeader}>
					{title && <h2 id={titleId.current} className={styles.drawerTitle}>{title}</h2>}
					<button
						className={styles.closeButton}
						onClick={onClose}
						aria-label="Close drawer"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							aria-hidden="true"
						>
							<path d="M18 6L6 18M6 6l12 12" />
						</svg>
					</button>
				</div>
				<div className={styles.drawerBody}>{children}</div>
			</div>
		</div>,
		document.body,
	);
};

export default Drawer;